import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { BehaviorSubject, catchError, Subject, tap, throwError } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';

import { environment } from '../../../environments/environment';
import { UserBasicModel } from '../models/user-basic.model';
import { CompaniesRequestModel } from '../models/companies-request.model';
import { WarningModalComponent } from '../../shared/warning-modal/warning-modal.component';
import { NewCompanyModel } from '../models/new-company.model';
import { CompanyBasicResponseModel } from '../models/company-basic-response.model';
import { CompanyDetailsModel } from '../models/company-details.model';
import { CompanyDetailsUpdateModel } from '../models/company-details-update.model';
import { UserResponseModel } from '../models/user-response.model';
import { PasswordResetResponseModel } from '../models/password-reset-response.model';
import { UserUpdateModel } from '../models/user-update.model';
import { SelfCompanyModel } from '../models/self-company.model';

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  private apiServerUrl: string = environment.apiBaseUrl;
  public selectedCompanyResponse$: BehaviorSubject<CompanyDetailsModel | null> =
    new BehaviorSubject<CompanyDetailsModel | null>(null);
  public companiesResponse$: Subject<CompanyBasicResponseModel> =
    new Subject<CompanyBasicResponseModel>();

  constructor(
    private http: HttpClient,
    private dialog: MatDialog
  ) {}

  public companiesReq: CompaniesRequestModel = {
    name: '',
    sort: 'name,desc',
    page: 0,
    size: 10,
  };
  public getAllCompanies(): void {
    const params: HttpParams = new HttpParams()
      .append('name', this.companiesReq.name)
      .append('sort', this.companiesReq.sort)
      .append('size', this.companiesReq.size)
      .append('page', this.companiesReq.page);
    this.http
      .get<CompanyBasicResponseModel>(`${this.apiServerUrl}/company`, {
        params,
      })
      .pipe(
        catchError(error => {
          this.handleError(error);
          return throwError(() => new Error(error));
        }),
        tap((companies: CompanyBasicResponseModel): void => {
          this.companiesResponse$.next(companies);
        })
      )
      .subscribe();
  }
  public setClientsFilter(filterValue: string): void {
    this.companiesReq.name = filterValue;
    this.getAllCompanies();
  }
  public setClientsSort(clientsSort: Sort): void {
    const property = clientsSort.active;
    const direction = clientsSort.direction;
    this.companiesReq.sort = `${property},${direction}`;
    this.getAllCompanies();
  }
  public setClientsPagination(pagination: PageEvent): void {
    this.companiesReq.size = pagination.pageSize;
    this.companiesReq.page = pagination.pageIndex;
    this.getAllCompanies();
  }

  public getCompanyDetails(companyId: number): void {
    this.http
      .get<CompanyDetailsModel>(`${this.apiServerUrl}/company/${companyId}`)
      .pipe(
        catchError(error => {
          this.handleError(error);
          return throwError(() => new Error(error));
        }),
        tap((companyDetails: CompanyDetailsModel): void => {
          this.selectedCompanyResponse$.next(companyDetails);
        })
      )
      .subscribe();
  }
  public createCompany(newCompany: NewCompanyModel): void {
    this.http
      .post<void>(`${this.apiServerUrl}/company`, newCompany)
      .pipe(
        catchError(error => {
          this.handleError(error);
          return throwError(() => new Error(error));
        })
      )
      .subscribe(() => this.getAllCompanies());
  }
  public updateCompany(companyDetails: CompanyDetailsUpdateModel): void {
    this.http
      .patch<void>(
        `${this.apiServerUrl}/company/${companyDetails.id}`,
        companyDetails
      )
      .pipe(
        catchError(error => {
          this.handleError(error);
          return throwError(() => new Error(error));
        })
      )
      .subscribe(() => {
        this.getAllCompanies();
        this.getCompanyDetails(companyDetails.id);
      });
  }
  public createNewUser(userInfo: UserBasicModel): void {
    this.http
      .post<UserBasicModel>(`${this.apiServerUrl}/auth/register`, userInfo)
      .pipe(
        catchError(error => {
          this.handleError(error);
          return throwError(() => new Error(error));
        })
      )
      .subscribe();
  }

  public getCompanyUsers(companyId: number) {
    return this.http
      .get<UserResponseModel[]>(`${this.apiServerUrl}/company/${companyId}/users`)
      .pipe(
        catchError(error => {
          this.handleError(error);
          return throwError(() => new Error(error));
        })
      );
  }

  public resetUserPassword(userId: number) {
    return this.http
      .post<PasswordResetResponseModel>(`${this.apiServerUrl}/users/${userId}/reset-password`, {})
      .pipe(
        catchError(error => {
          this.handleError(error);
          return throwError(() => new Error(error));
        })
      );
  }

  public updateUser(userId: number, updateData: UserUpdateModel) {
    return this.http
      .put<UserResponseModel>(`${this.apiServerUrl}/users/${userId}`, updateData)
      .pipe(
        catchError(error => {
          this.handleError(error);
          return throwError(() => new Error(error));
        })
      );
  }

  public getSubadminsOverview() {
    return this.http
      .get<any[]>(`${this.apiServerUrl}/company/subadmins-overview`)
      .pipe(
        catchError(error => {
          this.handleError(error);
          return throwError(() => new Error(error));
        })
      );
  }

  // ── Admin sam sebi bira tvrtku (za pregled dashboarda V3) ──────────────────
  //
  // Dashboard V3 razrješava tvrtku iz `_user.company_id`. Ove tri metode
  // postavljaju, čitaju i brišu taj stupac za PRIJAVLJENOG admina.

  /**
   * Pretraga tvrtki za dropdown. Namjerno ne dira `companiesResponse$` —
   * taj Subject puni tablicu klijenata na /admin/companies, pa bi pisanje u
   * njega mijenjalo i tu tablicu dok admin ovdje samo tipka u pretragu.
   */
  public searchCompanies(name: string) {
    const params: HttpParams = new HttpParams()
      .append('name', name)
      .append('sort', 'name,asc')
      .append('size', 8)
      .append('page', 0);
    return this.http
      .get<CompanyBasicResponseModel>(`${this.apiServerUrl}/company`, { params })
      .pipe(
        catchError(error => {
          this.handleError(error);
          return throwError(() => new Error(error));
        })
      );
  }

  public getSelfCompany() {
    return this.http
      .get<SelfCompanyModel>(`${this.apiServerUrl}/company/self-company`)
      .pipe(
        catchError(error => {
          this.handleError(error);
          return throwError(() => new Error(error));
        })
      );
  }

  public assignSelfCompany(companyId: number) {
    return this.http
      .put<SelfCompanyModel>(
        `${this.apiServerUrl}/company/self-company/${companyId}`,
        {}
      )
      .pipe(
        catchError(error => {
          this.handleError(error);
          return throwError(() => new Error(error));
        })
      );
  }

  public clearSelfCompany() {
    return this.http
      .delete<void>(`${this.apiServerUrl}/company/self-company`)
      .pipe(
        catchError(error => {
          this.handleError(error);
          return throwError(() => new Error(error));
        })
      );
  }

  public getCompanyAdmins(companyId: number) {
    return this.http
      .get<UserResponseModel[]>(`${this.apiServerUrl}/company/${companyId}/admins`)
      .pipe(
        catchError(error => {
          this.handleError(error);
          return throwError(() => new Error(error));
        })
      );
  }

  public getAllAdmins() {
    return this.http
      .get<UserResponseModel[]>(`${this.apiServerUrl}/company/admins`)
      .pipe(
        catchError(error => {
          this.handleError(error);
          return throwError(() => new Error(error));
        })
      );
  }

  public addAdminToCompany(companyId: number, userId: number) {
    return this.http
      .post<void>(`${this.apiServerUrl}/company/${companyId}/admins/${userId}`, {})
      .pipe(
        catchError(error => {
          this.handleError(error);
          return throwError(() => new Error(error));
        })
      );
  }

  public removeAdminFromCompany(companyId: number, userId: number) {
    return this.http
      .delete<void>(`${this.apiServerUrl}/company/${companyId}/admins/${userId}`)
      .pipe(
        catchError(error => {
          this.handleError(error);
          return throwError(() => new Error(error));
        })
      );
  }

  private handleError(error: HttpErrorResponse): void {
    const errorMessage = error.error.message;
    this.dialog.open(WarningModalComponent, {
      data: errorMessage,
    });
  }
}
